import { createSelector, createSlice } from '@reduxjs/toolkit';
import { translate } from '../../../hooks/useTranslations';
import { selectAllBusinessAreas } from './businessAreasSlice';
import { selectActiveLanguage, selectDefaultLanguageId, selectTranslations } from './localizationSlice';

const initialState = {
	all: null,
	categories: null,
	specialFilters: [
		{ id: 'URGENT', title: 'webapp_news_filter_special_urgent' },
	],
	activeNewsCategoryFilters: ['all'],
	activeBusinessAreaFilters: ['all'],
	activeSpecialFilters: [],
};

const newsSlice = createSlice({
	name: 'news',
	initialState,
	reducers: {
		setNews: (state, action) => {
			if (action?.payload?.news) {
				state.all = action.payload.news;
			}
		},
		setNewsCategories: (state, action) => {
			if (action?.payload?.newsCategories) {
				state.categories = action.payload.newsCategories;
			}
		},
		activateNewsCategoryFilter: (state, action) => {
			const categoryId = action?.payload?.categoryId;
			if (categoryId) {
				if (categoryId === 'all') {
					// deactivate 'all' checkmarks
					state.activeNewsCategoryFilters = ['all'];
				} else if (state.activeNewsCategoryFilters.indexOf(categoryId) === -1) {
					state.activeNewsCategoryFilters.push(categoryId);
					// Remove 'all' checkmark
					const index = state.activeNewsCategoryFilters.indexOf('all');
					if (index > -1) {
						state.activeNewsCategoryFilters.splice(index, 1);
					}
				}
			}
		},
		deactivateNewsCategoryFilter: (state, action) => {
			const categoryId = action?.payload?.categoryId;
			if (categoryId) {
				if (categoryId === 'all') {
					// do nothing
				}
				const index = state.activeNewsCategoryFilters.indexOf(categoryId);
				if (index > -1) {
					state.activeNewsCategoryFilters.splice(index, 1);
				}

				if (state.activeNewsCategoryFilters.length === 0) {
					state.activeNewsCategoryFilters = ['all'];
				}
			}
		},
		activateBusinessAreaFilter: (state, action) => {
			const businessAreaId = action?.payload?.businessAreaId;
			if (businessAreaId) {
				if (businessAreaId === 'all') {
					// deactivate 'all' checkmarks
					state.activeBusinessAreaFilters = ['all'];
				} else if (state.activeBusinessAreaFilters.indexOf(businessAreaId) === -1) {
					state.activeBusinessAreaFilters.push(businessAreaId);
					// Remove 'all' checkmark
					const index = state.activeBusinessAreaFilters.indexOf('all');
					if (index > -1) {
						state.activeBusinessAreaFilters.splice(index, 1);
					}
				}
			}
		},
		deactivateBusinessAreaFilter: (state, action) => {
			const businessAreaId = action?.payload?.businessAreaId;
			if (businessAreaId) {
				if (businessAreaId === 'all') {
					// do nothing
				}
				const index = state.activeBusinessAreaFilters.indexOf(businessAreaId);
				if (index > -1) {
					state.activeBusinessAreaFilters.splice(index, 1);
				}

				if (state.activeBusinessAreaFilters.length === 0) {
					state.activeBusinessAreaFilters = ['all'];
				}
			}
		},
		activateSpecialFilter: (state, action) => {
			const special = action?.payload?.special;
			if (special) {
				if (state.activeSpecialFilters.indexOf(special) === -1) {
					state.activeSpecialFilters.push(special);
				}
			}
		},
		deactivateSpecialFilter: (state, action) => {
			const special = action?.payload?.special;
			if (special) {
				const index = state.activeSpecialFilters.indexOf(special);
				if (index > -1) {
					state.activeSpecialFilters.splice(index, 1);
				}
			}
		},
		resetNews: (state) => {
			state.all = initialState.all;
			state.categories = initialState.categories;
			state.specialFilters = initialState.specialFilters;
			state.activeNewsCategoryFilters = initialState.activeNewsCategoryFilters;
			state.activeBusinessAreaFilters = initialState.activeBusinessAreaFilters;
			state.activeSpecialFilters = initialState.activeSpecialFilters;
		},
	},
});

export default newsSlice.reducer;
export const {
	setNews, setNewsCategories, activateNewsCategoryFilter, deactivateNewsCategoryFilter, activateBusinessAreaFilter, deactivateBusinessAreaFilter, activateSpecialFilter, deactivateSpecialFilter, resetNews,
} = newsSlice.actions;

export const selectAllNews = (state) => state.news.all;
export const selectNewsById = (state, newsId) => state.news.all?.[newsId];
export const selectNewsCategories = (state) => state.news.categories;
export const selectActiveNewsCategoryFilters = (state) => state.news.activeNewsCategoryFilters;
export const selectActiveBusinessAreaFilters = (state) => state.news.activeBusinessAreaFilters;
export const selectSpecialFilters = (state) => state.news.specialFilters;
export const selectActiveSpecialFilters = (state) => state.news.activeSpecialFilters;

export const selectNewsByIdWithObjects = createSelector(
	selectNewsById,
	selectAllBusinessAreas,
	selectNewsCategories,
	(news, allBusinessAreas, allNewsCategories) => {
		if (news) {
			const businessareas = news.businessarea_ids.map((baId) => allBusinessAreas.find((ba) => ba.id === baId));
			const newsCategories = news.news_category_ids.map((catId) => allNewsCategories?.[catId]);
			return { business_areas: businessareas, news_categories: newsCategories, ...news };
		}

		return null;
	},
);

export const selectAllNewsWithObjects = createSelector(
	selectAllNews,
	selectAllBusinessAreas,
	selectNewsCategories,
	(allNews, allBusinessAreas, allNewsCategories) => {
		if (allNews) {
			return Object.values(allNews).map((news) => {
				const businessareas = news.businessarea_ids.map((baId) => allBusinessAreas.find((ba) => ba.id === baId));
				const newsCategories = news.news_category_ids.map((catId) => allNewsCategories?.[catId]);
				return { business_areas: businessareas, news_categories: newsCategories, ...news };
			});
		}

		return null;
	},
);

export const selectFilteredNews = createSelector(
	selectAllNewsWithObjects,
	selectActiveNewsCategoryFilters,
	selectActiveBusinessAreaFilters,
	selectActiveSpecialFilters,
	(allNews, activeNewsCategoryFilters, activeBusinessAreaFilters, activeSpecialFilters) => {
		let filteredNews = allNews;
		if (activeNewsCategoryFilters.length > 0 && !activeNewsCategoryFilters.includes('all')) {
			filteredNews = filteredNews.filter((news) => {
				const hasActiveNewsCategory = news.news_category_ids.some((catId) => activeNewsCategoryFilters.includes(catId));
				return hasActiveNewsCategory;
			});
		}

		if (activeBusinessAreaFilters.length > 0 && !activeBusinessAreaFilters.includes('all')) {
			filteredNews = filteredNews.filter((news) => {
				const hasActiveBusinessArea = news.businessarea_ids.some((baId) => activeBusinessAreaFilters.includes(baId));
				return hasActiveBusinessArea;
			});
		}

		if (activeSpecialFilters.length > 0) {
			filteredNews = filteredNews.filter((news) => {
				if (activeSpecialFilters.includes('URGENT')) {
					if (news.urgent === '1') {
						return true;
					}
				}
				return false;
			});
		}

		const orderedNews = filteredNews?.sort((a, b) => new Date(b.date ?? 0) - new Date(a.date ?? 0));
		return orderedNews;
	},
);

export const selectNewsCategoryFilters = createSelector(
	selectNewsCategories,
	selectTranslations,
	selectActiveLanguage,
	selectDefaultLanguageId,
	(allNewsCategoriesKeyedById, allTranslations, activeLanguageId, defaultLanguageId) => {
		const allCategoriesObject = { id: 'all', title: 'webapp_news_filter_cats_all' };
		const allNewsCategories = Object.values(allNewsCategoriesKeyedById).map((news) => ({ id: news.id, title: news.title }));

		const sortedAllNewsCategories = allNewsCategories.sort((a, b) => translate(a.title, allTranslations, activeLanguageId, defaultLanguageId).localeCompare(translate(b.title, allTranslations, activeLanguageId, defaultLanguageId)));

		return [allCategoriesObject, ...sortedAllNewsCategories];
	},
);

export const selectAllNewsBusinessAreaIds = createSelector(
	selectAllNews,
	(allNews) => {
		const allBusinessAreaIds = Object.values(allNews)?.map((news) => news.businessarea_ids);
		const flatBusinessAreaIds = allBusinessAreaIds?.reduce((a, b) => a.concat(b), []);
		const businessAreaIdsWithoutDuplicates = [...new Set(flatBusinessAreaIds)];

		return businessAreaIdsWithoutDuplicates;
	},
);

export const selectBusinessAreasFilters = createSelector(
	selectAllNewsBusinessAreaIds,
	selectAllBusinessAreas,
	selectTranslations,
	selectActiveLanguage,
	selectDefaultLanguageId,
	(newsBusinessAreaIds, allBusinessAreas, allTranslations, activeLanguageId, defaultLanguageId) => {
		const allBusinessAreasObject = { id: 'all', title: 'webapp_news_filter_bas_all' };
		const businessAreas = newsBusinessAreaIds.map((businessAreaId) => {
			const businessArea = allBusinessAreas.find((ba) => ba.id === businessAreaId);
			if (businessArea) {
				return { id: businessArea.id, title: businessArea.translationkey };
			}
			return null;
		}).filter((entry) => entry !== null);
		const sortedBusinessAreas = businessAreas.sort((a, b) => translate(a.title, allTranslations, activeLanguageId, defaultLanguageId).localeCompare(translate(b.title, allTranslations, activeLanguageId, defaultLanguageId)));
		return [allBusinessAreasObject, ...sortedBusinessAreas];
	},
);
